import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, ArrowLeft, ShieldCheck, ShieldOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full border border-white/10 bg-[#12151C] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500";

export default function AdminSetup() {
  const [available, setAvailable] = useState(null);
  const [form, setForm] = useState({ code: "", email: "sagittariusmotorworks@gmail.com", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/admin/setup/status").then(({ data }) => setAvailable(data.available)).catch(() => setAvailable(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setError("");
    setLoading(true);
    try {
      await api.post("/admin/setup", { code: form.code, email: form.email, password: form.password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App flex min-h-screen items-center justify-center px-5" data-testid="admin-setup-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 sm:p-10"
      >
        <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Workshop Control — Set admin password</div>

        {available === null && <Loader2 size={18} className="mt-10 animate-spin text-amber-500" />}

        {available === false && !done && (
          <div className="mt-10 border border-white/10 bg-[#0D1017] px-5 py-6 text-sm text-slate-400" data-testid="admin-setup-unavailable">
            <ShieldOff size={22} className="text-slate-500" />
            <p className="mt-4">This one-time setup has already been used or is not enabled. Sign in with your staff credentials instead.</p>
            <Link to="/admin/login" className="mt-5 inline-block font-mono text-xs uppercase tracking-[0.2em] text-amber-500" data-testid="admin-setup-go-login">Go to staff login →</Link>
          </div>
        )}

        {done && (
          <div className="mt-10 border border-emerald-500/40 bg-emerald-500/10 px-5 py-6 text-sm text-emerald-300" data-testid="admin-setup-success">
            <ShieldCheck size={22} />
            <p className="mt-4">Admin password set for <span className="font-mono">{form.email}</span>. The setup code is now disabled.</p>
            <Link to="/admin/login" className="mt-5 inline-block font-mono text-xs uppercase tracking-[0.2em] text-amber-500" data-testid="admin-setup-success-login">Sign in to Workshop Control →</Link>
          </div>
        )}

        {available && !done && (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-setup-form">
            <div>
              <label className={labelCls}>One-time setup code</label>
              <input required value={form.code} onChange={set("code")} placeholder="XXXX-XXXX-XXXX" className={`${inputCls} font-mono uppercase tracking-[0.2em]`} autoComplete="off" data-testid="admin-setup-code-input" />
            </div>
            <div>
              <label className={labelCls}>Staff email</label>
              <input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="admin-setup-email-input" />
            </div>
            <div>
              <label className={labelCls}>New password (min 8 characters)</label>
              <input type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="••••••••" className={inputCls} autoComplete="new-password" data-testid="admin-setup-password-input" />
            </div>
            <div>
              <label className={labelCls}>Confirm password</label>
              <input type="password" required value={form.confirm} onChange={set("confirm")} placeholder="••••••••" className={inputCls} autoComplete="new-password" data-testid="admin-setup-confirm-input" />
            </div>

            {error && <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="admin-setup-error">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-setup-submit"
              className="flex w-full items-center justify-center gap-3 bg-amber-600 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              {loading ? "Saving…" : "Set admin password"}
            </button>
          </form>
        )}

        <Link to="/admin/login" className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500" data-testid="admin-setup-back">
          <ArrowLeft size={12} /> Back to staff login
        </Link>
      </motion.div>
    </div>
  );
}
